/**
 * What we actually learned when we tried to open a USB camera.
 *
 * Until now the probe answered one boolean, and every failure — the wrong
 * Windows driver, an unplugged body, a camera in MTP mode, a libusb we have
 * never seen before — collapsed into the same "Uninstalled/Occupied" label.
 * Those need opposite actions from the operator, so the probe reports which
 * one it was.
 */
export type CameraDriverState =
  /** Opened and claimed. WinUSB-class driver is bound; we can talk PTP to it. */
  | 'ready'
  /**
   * Opened, but something already holds it. On Windows that can only happen
   * once a WinUSB-class driver is bound, so the driver IS installed — in
   * practice the holder is our own renderer's live-view session.
   */
  | 'in-use'
  /**
   * libusb enumerated the device but cannot open it: the manufacturer's own
   * driver (Canon's, installed with EOS Utility) is bound instead of WinUSB.
   * Zadig's "Replace Driver" is the fix, and nothing else is.
   */
  | 'wrong-driver'
  /** It was in the device list a moment ago and is not there now. */
  | 'disconnected'
  /** A libusb error we have no advice for. Shown as-is rather than guessed at. */
  | 'unknown'

/**
 * Classify a libusb failure from `open()` / `claim()`.
 *
 * Only ACCESS/BUSY and NOT_SUPPORTED carry an unambiguous meaning; everything
 * else stays 'unknown' on purpose, because an inaccurate instruction ("run
 * Zadig") costs an operator a working driver.
 */
export function classifyDriverProbeError(error: unknown): Exclude<CameraDriverState, 'ready'> {
  const message = error instanceof Error ? error.message : String(error ?? '')

  if (/LIBUSB_ERROR_ACCESS|LIBUSB_ERROR_BUSY/.test(message)) return 'in-use'
  if (/LIBUSB_ERROR_NOT_SUPPORTED/.test(message)) return 'wrong-driver'
  if (/LIBUSB_ERROR_NO_DEVICE|LIBUSB_ERROR_NOT_FOUND/.test(message)) return 'disconnected'

  return 'unknown'
}

/**
 * Whether the app can expect to open this camera. Deliberately true for
 * 'in-use': that state proves the driver is present, and the holder is almost
 * always our own renderer, so blocking selection on it would break live
 * sessions.
 */
export function isCameraDriverUsable(state: CameraDriverState): boolean {
  return state === 'ready' || state === 'in-use'
}

/**
 * The i18n key the camera dropdown should show for a probe result.
 *
 * `state` is undefined when the entry did not come from a USB probe at all
 * (webcams, hand-built fixtures); we then fall back to the old boolean so
 * nothing in the list renders blank.
 */
export function cameraDriverStatusKey(
  state: CameraDriverState | undefined,
  hasDriver: boolean | undefined
): string {
  switch (state) {
    case 'ready':
    case 'in-use':
      return 'settings.camera.driverStatus.installed'
    case 'wrong-driver':
      return 'settings.camera.driverStatus.wrongDriver'
    case 'disconnected':
      return 'settings.camera.driverStatus.disconnected'
    case 'unknown':
      return 'settings.camera.driverStatus.unknown'
    default:
      return hasDriver
        ? 'settings.camera.driverStatus.installed'
        : 'settings.camera.driverStatus.uninstalled'
  }
}
